import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	IconButton,
	Paper,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableFooter,
	TablePagination,
	TableRow,
	Tooltip,
	Typography,
} from '@mui/material';
import * as React from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useAuthContext } from '../../../../../context/auth';
import { dayjs } from '../../../../../date-util';
import TablesAPI, { TableResponse } from '../../api/tables';
import {
	MODULAR_CONFIG_UPLOAD_DIALOG_WIDTH,
	TABLES_ACTIONS_DELETE_TOOLTIP_TEXT,
	TABLES_ACTIONS_DOWNLOAD_TOOLTIP_TEXT,
	TABLES_CONFIRM_DELETE_MODAL_CANCEL_BUTTON,
	TABLES_CONFIRM_DELETE_MODAL_DELETE_BUTTON,
	TABLES_CONFIRM_DELETE_MODAL_HEADER,
	TABLES_CONFIRM_DELETE_MODAL_MESSAGE,
	TABLES_HEADER,
	TABLES_HELPER_TEXT,
	TABLES_LAST_UPDATED_LABEL,
	TABLES_MODAL_HEADER,
	TABLES_REFRESHING_LABEL,
	TABLES_ROW_DATE_FORMAT,
	TABLES_SUBHEADER_ACTIONS,
	TABLES_SUBHEADER_EFFECTIVE_DATE,
	TABLES_SUBHEADER_TABLE_NAME,
} from '../../data/constants';
import { handleModalClose } from '../../utils/handleModalClose';
import AddTablesForm from './components/AddTablesForm';
import FilterTables from './components/FilterTable';
import TableHeader, { HeadCell, Order } from './components/TableHeader';

export const TABLE_KEY = 'tables';

const headCells: HeadCell[] = [
	{
		id: 'tableName',
		label: TABLES_SUBHEADER_TABLE_NAME(),
	},
	{
		id: 'effectiveTimestamp',
		label: TABLES_SUBHEADER_EFFECTIVE_DATE(),
	},
	{
		id: 'locator',
		disableSort: true,
		align: 'right',
		label: TABLES_SUBHEADER_ACTIONS(),
	},
];

export const useFilterParams = () => {
	const locationSearch = useLocation().search;
	const params = new URLSearchParams(locationSearch);

	return {
		tableName: params.get('tableName') ?? '',
		asOfDate: params.get('asOfDate') ?? '',
	};
};

export default function Tables() {
	const { tenantHostname } = useParams();
	const navigate = useNavigate();
	const location = useLocation();
	const authData = useAuthContext();
	const authToken = authData?.tenantAuthorizationData?.authorizationToken;
	const tenantId = authData?.tenantAuthorizationData?.tenantId;
	const { asOfDate, tableName } = useFilterParams();
	const { data, isLoading, isRefetching, dataUpdatedAt, refetch } =
		TablesAPI.useTables(
			[TABLE_KEY, tenantId || tenantHostname, asOfDate, tableName],
			{
				asOfTimestamp: parseInt(asOfDate, 10),
				tableName: tableName,
			},
			authToken,
			tenantHostname,
		);

	const { mutateAsync: deleteTable, isLoading: isDeleting } =
		TablesAPI.useDeleteTableMutation(
			[TABLE_KEY, tenantId || tenantHostname, asOfDate, tableName],
			authToken,
			tenantHostname,
		);

	const [order, setOrder] = React.useState<Order>('asc');
	const [orderBy, setOrderBy] =
		React.useState<keyof TableResponse>('effectiveTimestamp');
	const [page, setPage] = React.useState(0);
	const [rowsPerPage, setRowsPerPage] = React.useState(50);
	const [isTableModalOpen, setIsTableModalOpen] = React.useState(false);
	const [isConfirmDeleteModalOpen, setIsConfirmDeleteModalOpen] =
		React.useState(false);
	const [tablePendingDelete, setTablePendingDelete] =
		React.useState<TableResponse>();

	const handleTableModalOpen = () => {
		setIsTableModalOpen(true);
	};

	const handleTableModalClose = () => {
		setIsTableModalOpen(false);
	};

	const handleConfirmDeleteModalOpen = (selectedTable: TableResponse) => {
		setIsConfirmDeleteModalOpen(true);
		setTablePendingDelete(selectedTable);
	};

	const handleConfirmDeleteModalClose = () => {
		setIsConfirmDeleteModalOpen(false);
		setTablePendingDelete(null);
	};

	const handleChangePage = (event: unknown, newPage: number) => {
		setPage(newPage);
	};

	const handleChangeRowsPerPage = (
		event: React.ChangeEvent<HTMLInputElement>,
	) => {
		setRowsPerPage(parseInt(event.target.value, 10));
		setPage(0);
	};

	const handleRequestSort = (
		event: React.MouseEvent<unknown>,
		property: keyof TableResponse,
	) => {
		const isAsc = orderBy === property && order === 'asc';
		setOrder(isAsc ? 'desc' : 'asc');
		setOrderBy(property);
	};

	const handleDeleteTable = async (table: TableResponse) => {
		await deleteTable(table.locator);
		handleConfirmDeleteModalClose();
	};

	return (
		<>
			<Stack direction='column' height='100%' spacing={2}>
				<Stack direction='row' spacing={2}>
					<Typography variant='h3'>{TABLES_HEADER()}</Typography>
					<Button
						variant='contained'
						color='secondary'
						startIcon={<AddIcon />}
						onClick={handleTableModalOpen}
					>
						{TABLES_MODAL_HEADER()}
					</Button>
				</Stack>
				<FilterTables
					asOfDate={parseInt(asOfDate, 10)}
					tableName={tableName}
					onApply={({ asOfDate, tableName }) => {
						const params = new URLSearchParams({});

						if (asOfDate) {
							params.set('asOfDate', asOfDate + '');
						}
						if (tableName) {
							params.set('tableName', tableName);
						}
						navigate(location.pathname + '?' + params.toString());
					}}
					refetch={refetch}
				/>

				<Stack direction='row' spacing={2} justifyContent='space-between'>
					<Stack
						direction='row'
						spacing={1}
						alignContent='center'
						color='#777777'
					>
						<InfoOutlinedIcon fontSize='small' />
						<Typography variant='body2' align='left'>
							{TABLES_HELPER_TEXT()}
						</Typography>
					</Stack>
					<Typography variant='body2' align='right'>
						{isLoading ? TABLES_REFRESHING_LABEL() : null}
						{!isLoading && isRefetching ? TABLES_REFRESHING_LABEL() : null}
						{!isLoading && !isRefetching
							? TABLES_LAST_UPDATED_LABEL() +
							  dayjs(dataUpdatedAt).local().format('lll')
							: null}
					</Typography>
				</Stack>

				<Paper
					sx={{
						flex: 1,
						overflowY: 'auto',
					}}
				>
					<TableContainer sx={{ maxHeight: '100%' }}>
						<Table size='small' stickyHeader>
							<TableHeader
								headCells={headCells}
								order={order}
								orderBy={orderBy}
								onRequestSort={handleRequestSort}
							/>
							<TableBody>
								{data
									?.sort((a, b) => {
										const direction = order === 'asc' ? 1 : -1;
										if (a[orderBy] < b[orderBy]) {
											return -1 * direction;
										}
										if (a[orderBy] > b[orderBy]) {
											return 1 * direction;
										}
										return 0;
									})
									.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
									.map((table, index) => (
										<TableRow
											key={table.locator}
											hover
											sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
										>
											<TableCell>{table.tableName}</TableCell>
											<TableCell sx={{ minWidth: 280 }}>
												<Typography fontWeight={500} variant='body2'>
													{dayjs(table.effectiveTimestamp).format(
														TABLES_ROW_DATE_FORMAT(),
													)}
												</Typography>
											</TableCell>
											<TableCell>
												<Stack
													direction='row'
													spacing={1}
													justifyContent='flex-end'
												>
													<Tooltip
														title={TABLES_ACTIONS_DOWNLOAD_TOOLTIP_TEXT()}
													>
														<IconButton
															aria-label='download table'
															href={table.url}
														>
															<DownloadIcon />
														</IconButton>
													</Tooltip>
													<Tooltip title={TABLES_ACTIONS_DELETE_TOOLTIP_TEXT()}>
														<IconButton
															aria-label='delete'
															onClick={() =>
																handleConfirmDeleteModalOpen(table)
															}
														>
															<DeleteIcon />
														</IconButton>
													</Tooltip>
												</Stack>
											</TableCell>
										</TableRow>
									))}
							</TableBody>
							{data && !isLoading && data.length > 0 && (
								<TableFooter>
									<TableRow>
										<TableCell
											colSpan={3}
											sx={{
												backgroundColor: 'white.lightest',
												position: 'sticky',
												bottom: 0,
												borderTop: '1px solid',
												borderColor: 'white.dark',
											}}
										>
											<TablePagination
												rowsPerPageOptions={[10, 25, 50]}
												component='div'
												count={data?.length}
												rowsPerPage={rowsPerPage}
												page={page}
												onPageChange={handleChangePage}
												onRowsPerPageChange={handleChangeRowsPerPage}
											/>
										</TableCell>
									</TableRow>
								</TableFooter>
							)}
						</Table>
					</TableContainer>
				</Paper>
			</Stack>
			<Dialog
				open={isConfirmDeleteModalOpen}
				onClose={(e, r) => handleModalClose(r, handleConfirmDeleteModalClose)}
			>
				<DialogTitle>{TABLES_CONFIRM_DELETE_MODAL_HEADER()}</DialogTitle>
				<DialogContent sx={{ width: MODULAR_CONFIG_UPLOAD_DIALOG_WIDTH }}>
					{TABLES_CONFIRM_DELETE_MODAL_MESSAGE(tablePendingDelete?.tableName)}
				</DialogContent>
				<DialogActions>
					<Button onClick={handleConfirmDeleteModalClose} color='neutralButton'>
						{TABLES_CONFIRM_DELETE_MODAL_CANCEL_BUTTON()}
					</Button>
					<Button
						variant='contained'
						color='error'
						disabled={isDeleting}
						onClick={() => handleDeleteTable(tablePendingDelete)}
					>
						{TABLES_CONFIRM_DELETE_MODAL_DELETE_BUTTON()}
					</Button>
				</DialogActions>
			</Dialog>
			<Dialog
				open={isTableModalOpen}
				onClose={(e, r) => handleModalClose(r, handleTableModalClose)}
				sx={{ overflowX: 'hidden' }}
			>
				<DialogTitle>{TABLES_MODAL_HEADER()}</DialogTitle>
				<DialogContent sx={{ width: MODULAR_CONFIG_UPLOAD_DIALOG_WIDTH }}>
					<AddTablesForm
						tenantHostname={tenantHostname}
						handleClose={handleTableModalClose}
					/>
				</DialogContent>
			</Dialog>
		</>
	);
}
